import { ColumnConfig } from '@art-of-hospitality-solutions-llc/ui-kit/components/data-table';
import { ResourceTableView } from '@Components/ResourceTable';
import { DateTime } from 'luxon';
import type { Fish } from '@Types/fish';
import { buildFilterParams } from '@Utils/buildFilterParams';
import { useActiveTeamMembers, useTeamMembers } from '@Hooks/useTeamMembers';
import { useNavigate } from 'react-router-dom';
import { useResourceTableController } from '@Components/ResourceTable/useResourceTableController';

const getEmployeeProfile = (row) => {
  const employeeProfile = row.fishProfiles.find(
    (profile) => profile.profileType === 'EMPLOYEE',
  );
  if (employeeProfile) {
    return employeeProfile;
  } else {
    return false;
  }
};

const formatActiveStatus = (profile) => {
  if (profile?.employee_status === 'ACTIVE') {
    return 'Active';
  } else if (profile?.employee_status === 'TERMINATED') {
    return 'Terminated';
  } else {
    return '-';
  }
};

export const employeeColumnConfigs: ColumnConfig<Fish>[] = [
  {
    id: 'fName',
    headerLabel: 'First Name',
    accessor: (row) =>
      !getEmployeeProfile(row) ? '-' : getEmployeeProfile(row).fName || '-',
    type: 'text',
    size: 120,
    enableSorting: false,
  },
  {
    id: 'lName',
    headerLabel: 'Last Name',
    accessor: (row) =>
      !getEmployeeProfile(row) ? '-' : getEmployeeProfile(row).lName || '-',
    type: 'text',
    size: 120,
    enableSorting: false,
  },
  {
    id: 'email',
    headerLabel: 'Email',
    accessor: (row) =>
      !getEmployeeProfile(row) ? '-' : getEmployeeProfile(row)?.email || '-',
    type: 'text',
    enableSorting: false,
  },
  {
    id: 'phoneNumber',
    headerLabel: 'Phone Number',
    accessor: (row) => row.phoneNumber ?? '',
    type: 'text',
    cell: (info) => {
      const value = info.getValue();
      return value ? formatPhoneNumber(value) : '-';
    },
    enableSorting: true,
  },
  {
    id: 'employee_start_date',
    headerLabel: 'Start Date',
    accessor: (row) =>
      !getEmployeeProfile(row)
        ? '-'
        : getEmployeeProfile(row)?.employee_start_date || '-',
    type: 'text',
    cell: (info) => {
      const date = info.getValue();
      if (!date) return '-';
      const dt = DateTime.fromISO(date);
      const dateString = dt.toLocaleString(DateTime.DATETIME_MED);
      return dateString || '-';
    },
    enableSorting: false,
  },
  {
    id: 'employee_status',
    headerLabel: 'Employment Status',
    accessor: (row) =>
      !getEmployeeProfile(row)
        ? formatActiveStatus(row?.fishProfiles[0])
        : formatActiveStatus(getEmployeeProfile(row)) || '-',
    type: 'text',
    enableSorting: false,
  },
  {
    id: 'employee_department',
    headerLabel: 'Department',
    accessor: (row) =>
      !getEmployeeProfile(row)
        ? '-'
        : getEmployeeProfile(row)?.employee_department || '-',
    type: 'text',
    enableSorting: false,
  },
  {
    id: 'employee_position',
    headerLabel: 'Position',
    accessor: (row) =>
      !getEmployeeProfile(row)
        ? '-'
        : getEmployeeProfile(row)?.employee_position || '-',
    type: 'text',
    enableSorting: false,
  },
  {
    id: 'employee_venue',
    headerLabel: 'Venue',
    accessor: (row) =>
      !getEmployeeProfile(row)
        ? '-'
        : getEmployeeProfile(row)?.employee_venue || '-',
    type: 'text',
    enableSorting: false,
  },
];

function formatPhoneNumber(input) {
  const digits = input.replace(/\D/g, '');
  if (digits.length === 10) {
    return digits.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
  }
  return input;
}

const TeamMembersList = () => {
  const navigate = useNavigate();

  const FISH_FIELD_MAPPING: Record<string, string> = {
    fName: 'filter[fishProfiles][some][fName]',
    lName: 'filter[fishProfiles][some][lName]',
    email: 'filter[fishProfiles][some][email]',
    phoneNumber: 'filter[phoneNumber]',
    employee_status: 'filter[fishProfiles][some][employee_status]',
    employee_department: 'filter[fishProfiles][some][employee_department]',
    employee_position: 'filter[fishProfiles][some][employee_position]',
    employee_venue: 'filter[fishProfiles][some][employee_venue]',
  };

  const FISH_STATIC_PARAMS: Record<string, string> = {
    'include[subscriptions][filter][cancelledAt]': 'null',
    'include[subscriptions][filter][deletedAt]': 'null',
    'include[fishProfiles][filter][deletedAt]': 'null',
  };

  const controller = useResourceTableController<Fish>({
    columnsConfig: employeeColumnConfigs,
    defaultSort: {
      id: 'createdAt',
      desc: true,
    },
    baseSearchParams: () => ({
      'include': [
        'subscriptions.subscriptionTier',
        'sharedSubscription.subscription.subscriptionTier',
        'fishProfiles',
      ].join(','),
      'filter[fishProfiles][some][profileType]': `EMPLOYEE`.toUpperCase(),
    }),
    buildFilterParams: (filters) => {
      const uppercase = ['employee_status'];

      for (const i in filters) {
        const filter = filters[i];
        const filterValue = filter.value as
          | { operator: string; value: string }
          | undefined;

        if (uppercase.includes(filter.id) && filterValue?.value) {
          filterValue.value = filterValue.value.toUpperCase();
          filter.value = filterValue;
        }

        filters[i] = filter;
      }

      return buildFilterParams(filters, {
        fieldMapping: FISH_FIELD_MAPPING,
        staticParams: FISH_STATIC_PARAMS,
      });
    },
    quickFilterFields: [
      { field: 'fName', label: 'First Name' },
      { field: 'lName', label: 'Last Name' },
      { field: 'email', label: 'Email' },
      { field: 'phoneNumber', label: 'Phone Number' },
    ],
    initialPageSize: 10,
    initialPinning: {
      left: ['select', 'fName', 'lName'],
      right: [],
    },
  });

  const { data: payload, status } = useTeamMembers({
    searchParams: controller.searchParams,
  });

  const employees = payload?.data ?? [];
  const { data: activePayload } = useActiveTeamMembers({
    searchParams: controller.searchParams,
  });

  const rows: Fish[] = employees;
  const meta = payload?.meta ?? {};
  meta.activeTotal = activePayload?.meta?.total ?? 0;

  return (
    <ResourceTableView<Fish>
      label="Team Members"
      summaryLabel="Currently Active Team Members "
      controller={controller}
      data={rows}
      meta={meta}
      status={status as any}
      onRowClick={(row) => navigate(`/team-members/${row.id}`)}
    />
  );
};

export default TeamMembersList;
